// src/api/dto/loan-information.dto.ts

export enum LIGHT_REACH_ACCOUNT_STATUS {
  CREATED = `1 - created`,
}

export enum LIGHT_REACH_PROGRAM_TYPE {
  SOLAR = `solar`,
}

export type CreateAccountResponseDto = {
  id: string
  proposalId: string
  lightReachId: string
  organizationId: string
  status: LIGHT_REACH_ACCOUNT_STATUS
  programType: LIGHT_REACH_PROGRAM_TYPE
  createdAt: string
  updatedAt: string
}

export type MonthlyPayment = {
  year: number
  monthlyPayment: number
  totalMonthlyPayment: number
  backupBatteryPayment: number
}

export type LightReachPricing = {
  productId: string
  type: string
  name: string
  escalationRate: number
  kwhRate: number
  ppwRate: number
  monthlyPayments: MonthlyPayment[]
  programType: string
  description: string
  lseId: number
  utilityName: string
  state: string
  totalEPCPayment: number
}

export type LightReachPricingResponse = Map<string, LightReachPricing[]>

export type LightReachDisclosureDto = {
  id: string
  name: string
  type: string
  version: string
  versionNumber: number
  summary: string
  text: string
  requireConsent: boolean
  acceptanceLabel: string
  language: string
}

// Credit Application DTOs
export enum LIGHT_REACH_APPLICANT_TYPE {
  PRIMARY = `primary`,
  COAPPLICANT = `secondary`,
}

export type LightReachAddressDto = {
  address1: string
  city: string
  state: string
  zip: string
}

export type LightReachCreditApplicationDisclosureDto = {
  id: string
  type: string
  version: string
  accepted: boolean
}

export type LightReachCreditApplicationApplicantDto = {
  type: LIGHT_REACH_APPLICANT_TYPE
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  address: LightReachAddressDto
  ssn: string
  disclosures: LightReachCreditApplicationDisclosureDto[]
}

export type LightReachCreditApplicationRequestDto = {
  applicants: LightReachCreditApplicationApplicantDto[]
}

export type LightReachCreditApplicationStipulationDto = {
  stipulationType: string
  description: string
  isSatisfied: boolean
  requiresReview: boolean
}

export type LightReachCreditApplicationResponseDto = {
  id: string
  applicants: LightReachCreditApplicationApplicantDto[]
  propertyOwnershipStatus: string
  status: string
  stipulations: LightReachCreditApplicationStipulationDto[]
  creditExpiryDate?: string
}

// Quote Status Enum
export enum LIGHT_REACH_QUOTE_STATUS {
  ACTIVE = `active`,
  VOIDED = `voided`,
  INVALID = `invalid`,
}

// Quote DTO for LightReach quotes
export type LightReachQuoteDto = {
  id: string
  proposalId: string
  lightReachQuoteId: string
  lightReachAccountId: string
  productId: string
  systemFirstYearProductionKwh: number
  systemSizeKw: number
  kwhPrice: number
  totalSystemCost: number
  pricePerWatt: number
  preConAdderCost: number
  totalAmountPaid: number
  yearOneMonthlyPayment: number
  status: LIGHT_REACH_QUOTE_STATUS
  externalReference: string
  monthlyPayments: number[]
  createdAt: string
  updatedAt: string
  isValid: boolean
  backupBatteryPayment?: number | null
}

// Application Response DTOs
export type LightReachApplicationDisclosureResponseDto = {
  id: string
  disclosureType: string
  version: string
  accepted: boolean
  createdAt: string
}

export type LightReachApplicationApplicantResponseDto = {
  id: string
  applicantType: string
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  address1: string
  city: string
  state: string
  zip: string
  disclosures: LightReachApplicationDisclosureResponseDto[]
  createdAt: string
  updatedAt: string
}

export type LightReachApplicationStipulationResponseDto = {
  id: string
  stipulationType: string
  description: string
  isSatisfied: boolean
  requiresReview: boolean
  createdAt: string
  updatedAt: string
}

export type LightReachApplicationResponseDto = {
  id: string
  proposalId: string
  lightReachAccountId: string
  lightReachApplicationId: string
  propertyOwnershipStatus: string
  status: string
  creditExpiryDate?: string
  applicants: LightReachApplicationApplicantResponseDto[]
  stipulations: LightReachApplicationStipulationResponseDto[]
  createdAt: string
  updatedAt: string
}

// Contract Status Enum
export enum LIGHT_REACH_APPLICATION_STATUS {
  APPROVED = `approved`,
  APPROVED_WITH_STIPS = `approvedWithStipulations`,
  CREDIT_FROZEN = `creditFrozen`,
  DECLINED = `declined`,
  EXPIRED = `expired`,
}
export enum LIGHT_REACH_CONTRACT_STATUS {
  CREATED = `created`,
  SENT = `sent`,
  PARTIALLY_SIGNED = `partiallySigned`,
  SIGNED = `signed`,
  APPROVED = `approved`,
  REJECTED = `rejected`,
  VOIDED = `voided`,
  DISCLOSURE_SENT = `disclosureSent`,
  DISCLOSURE_SIGNED = `disclosureSigned`,
}

// Contract DTO
export type LightReachContractListDto = {
  id: string
  lightReachContractId: string
  lightReachAccountId: string
  proposalId: string
  status: string
  quoteId?: string
  externalReference?: string
  contractUrl?: string
  signedAt?: string
  createdAt: string
  updatedAt: string
}

// Validation DTOs
export type LightReachRangeValidationResult = {
  valid: boolean
  percentDifference: number
  difference: number
}

export type LightReachValidationReason = {
  reason: string
  field: string
}

export type LightReachValidationResponse = {
  status: string
  rangeValidationResult: LightReachRangeValidationResult
  reasons: LightReachValidationReason[]
}

// Available Adders DTOs
export enum LIGHT_REACH_ADDER_TYPE {
  ARBITRAGE_BATTERY = `arbitrageBattery`,
  ARBITRAGE_BATTERY_COST = `arbitrageBatteryCost`,
  ELECTRICAL_UPGRADE = `electricalUpgrade`,
  BACKUP_BATTERY = `backupBattery`,
  BACKUP_BATTERY_KWH = `backupBatteryKwh`,
  BACKUP_BATTERY_PRICE_CAP = `backupBatteryPriceCap`,
  BATTERY_MINIMUM_CAPACITY = `batteryMinimumCapacity`,
  SOLAR_READINESS = `solarReadiness`,
  ARBITRAGE_BATTERY_PRICE_CAP = `arbitrageBatteryPriceCap`,
}

export enum LIGHT_REACH_VALUE_TYPE {
  ADDITIONAL_KWH_RATE_BAND = `additionalKwhRateBand`,
  FIXED_COST = `fixedCost`,
  PERCENTAGE = `percentage`,
}

export type LightReachAdder = {
  adderType: LIGHT_REACH_ADDER_TYPE
  enabled: boolean
  valueType: LIGHT_REACH_VALUE_TYPE
  value: number
}

export type LightReachAvailableAddersDto = {
  availableAdders: LightReachAdder[]
}
